import { PageTransition } from "@/components/PageTransition";
import { ResultDisplay } from "@/components/ResultDisplay";
import { ResultActions } from "@/components/ResultActions";
import { DownloadHelpPopover } from "@/components/DownloadHelpPopover";
import { usePollPhotoResult } from "@/hooks/api/usePollPhotoResult";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { LoadingPage } from "./Loading";
import { JobStatusResponseDto } from "@/api";
import { AppError } from "@/lib/errorUtils";
import { usePaymentStore } from "@/lib/store/paymentStore";
import { useUnmount } from "usehooks-ts";
import { SOCIAL_LINE_URL } from "@/lib/constants/social";
import { useSEO } from "@/lib/seo/useSEO";

export const ResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("jobId");
  const [isPrinting, setIsPrinting] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [viewMode, setViewMode] = useState<"image" | "gif">("image");
  const navigate = useNavigate();
  const { resetPaymentPayload } = usePaymentStore();

  // Fetch job status
  const { data: jobStatus, isPending, error } = usePollPhotoResult(jobId ?? "");

  // SEO configuration
  useSEO({
    title: "Your Photo Result - KeptScene",
    description:
      "View and download your photo booth result. Share your memories with friends and family.",
    keywords: "photo result, photo booth result, download photo, KeptScene",
    image: jobStatus?.outputPath || jobStatus?.gifPath,
    noindex: true,
  });

  // Action handlers
  const handleReportIssue = () => {
    window.open(SOCIAL_LINE_URL, "_blank");
  };

  const handleExploreMore = () => {
    navigate("/photo-booth");
  };

  // Printing animation effect
  useEffect(() => {
    if (isPending) return;

    // Start printing animation after component mounts
    const printingTimer = setTimeout(() => {
      setShowResult(true);
    }, 1000);

    // Stop printing animation after result is fully shown
    const completeTimer = setTimeout(() => {
      setIsPrinting(false);
    }, 4000);

    return () => {
      clearTimeout(printingTimer);
      clearTimeout(completeTimer);
    };
  }, [isPending, jobStatus]);

  // Cleanup on unmount
  useUnmount(() => {
    resetPaymentPayload();
  });

  // Error handling
  if (error) {
    throw new AppError(
      error.message
        ? `${error.message} ${jobId}`
        : "Failed to get photo result" + jobId,
      "processing",
    );
  }

  if (jobStatus?.status === JobStatusResponseDto.status.FAILED) {
    throw new AppError(
      jobStatus.error
        ? `${jobStatus.error} ${jobId}`
        : "Failed to get photo result" + jobId,
      "processing",
    );
  }

  // Redirects
  if (!jobId) {
    return <Navigate to="/photo-booth" />;
  }

  if (isPending) {
    return <LoadingPage isLoading={true} />;
  }

  return (
    <PageTransition className="bg-[url('/result-animation/aug/background.jpg')] bg-cover bg-repeat min-h-screen">
      <div className="flex flex-col items-center justify-center px-4 pb-8">
        {/* Printer Container */}
        <div className="relative flex flex-col items-center w-full">
          {/* Printer SVG */}
          <div
            className={`relative bg-transparent w-full z-20 pt-8 ${
              isPrinting ? "printer-working" : ""
            }`}
          >
            <img
              src="/printer-2.svg"
              alt=""
              className="w-full h-auto"
              loading="lazy"
              aria-hidden="true"
            />
          </div>
          <div className="relative z-10 w-full min-h-[60vh]">
            {/* hidden due to FEB theme does not have animation in result page, we have to hide */}
            <img
              src="/result-animation/feb/result.png"
              alt=""
              className="w-full h-auto hidden"
              loading="lazy"
              aria-hidden="true"
            />
          </div>

          {/* Photo Result - slides out from printer */}
          <div className="absolute z-10 w-full top-[20%] left-[50%] -translate-x-1/2 mx-2 mt-4 flex flex-col justify-center items-center">
            <ResultDisplay
              showResult={showResult}
              viewMode={viewMode}
              jobStatus={jobStatus}
              onViewModeChange={setViewMode}
              isPrinting={isPrinting}
            />
          </div>
        </div>

        {!isPrinting && (
          <>
            <ResultActions
              jobStatus={jobStatus}
              viewMode={viewMode}
              onReportIssue={handleReportIssue}
              onExploreMore={handleExploreMore}
            />
            <DownloadHelpPopover />
          </>
        )}
      </div>
    </PageTransition>
  );
};
